import {BehaviorSubject, forkJoin, Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';

import fixArray from '../../../../../utils/fixArray';

import {ICruiseSearchParams} from '../interfaces/icruise-search-params';
import {MappingItem} from '../../../../shared/interfaces/mapping-item';
import {ICruiseSolution} from '../interfaces/icruise-solution';
import {ICruiseSpecial} from '../interfaces/icruise-special';
import {ICruiseCard} from '../interfaces/icruise-card';
import {ICruiseStep} from '../interfaces/icruise-step';

import {EnvironmentService} from '../../../../shared/services/environment.service';
import {ProjectService} from '../../../../shared/services/project.service';

import {CruiseTopId} from '../../../../shared/models/cruise-top-id.enum';

@Injectable({
  providedIn: 'root'
})
export class CruiseSearchService {
  private engineCruiseOpeningStatus: boolean = false;
  engineCruiseOpeningStatus$ = new BehaviorSubject<boolean>(this.engineCruiseOpeningStatus)

  httpCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  constructor(
    private _http: HttpClient,
    private _projectService: ProjectService,
    private _environmentService: EnvironmentService
  ) {}

  updateEngineCruiseOpeningStatus(newStatus: boolean) {
    this.engineCruiseOpeningStatus$.next(newStatus);
  }

  private getFormattedCruiseName(cruiseTopId: number, cruiseName: string): string {
    // se è una crociera carnival, rimuovo i caratteri speciali dalla stringa
    if (cruiseTopId === CruiseTopId.CARNIVAL) {
      const itineraryString = cruiseName;
      const withoutSquareBrackets = itineraryString.replace(/\[(.*?)\]/g, '');
      const withoutDestination = withoutSquareBrackets.replace(/#.+/g, '');
      const finalItineraryString = withoutDestination;

      return finalItineraryString;
    }

    return cruiseName;
  }

  formatShip(ship: any): MappingItem {
    return {
      id: ship?.Id,
      image: ship?.Image,
      name: ship?.Name,
      code: ship?.Code
    };
  }

  formatTop(top: any): MappingItem {
    return {
      id: top?.Id,
      name: top?.Name
    };
  }

  formatHarbour(harbour: any): MappingItem {
    return {
      id: harbour?.Id,
      name: harbour?.Name,
      code: harbour?.Code
    };
  }

  formatSpecial(special: any): ICruiseSpecial {
    return {
      id: special?.Id,
      type: special?.Type
    };
  }

  formatSolution(solution: any): ICruiseSolution {
    return {
      code: solution?.Code,
      departureDate: new Date(solution?.DepartureDate),
      departureHarbour: this.formatHarbour(solution?.DepartureHarbour),
      arrivalDate: new Date(solution?.ArrivalDate),
      arrivalHarbour: this.formatHarbour(solution?.ArrivalHarbour),
      hasFlight: solution?.FlightMandatory === 1,
      id: solution?.Id,
      price: parseFloat(solution?.MKPriceCP),
      source: solution?.Source,
      fare: fixArray(solution?.Cabin_Collection?.Cabin)?.find(
        (cabin) => Number(cabin?.MKPriceCP) === Number(solution?.MKPriceCP)
      )?.Fare?.toUpperCase(),
      specials: solution?.Specials_Collection?.Special
        ? fixArray(solution?.Specials_Collection?.Special).map((e) =>
            this.formatSpecial(e)
          )
        : undefined
    };
  }

  hasSpecial(special: any, specialToSearch: number) : boolean {
    return special
      ? fixArray(special).filter((e) =>
        { return (e.Id === specialToSearch) } )?.length > 0 : false;
  }

  formatStep(step: any): ICruiseStep {
    return {
      code: step.Code,
      harbour: step.Harbour,
      lat: step.Lt,
      lng: step.Ll
    };
  }

  formatCruise(cruise: any): ICruiseCard {
    return {
      code: cruise.Code,
      id: cruise.Id,
      seller: cruise.Seller,
      bestSolution: undefined,
      filteredSolutions: [],
      ship: this.formatShip(cruise.Ship),
      solutions: cruise.Solution_Collection.CruiseSolution ?
        fixArray(cruise.Solution_Collection.CruiseSolution).map(e => this.formatSolution(e)) : undefined,
      source: cruise.Source,
      title: this.getFormattedCruiseName(cruise.TourOperator?.Id, cruise.Name),
      steps: cruise.Steps_Detail.Step ?
        fixArray(cruise.Steps_Detail.Step).map(e => this.formatStep(e)) : undefined,
      tourOperator: this.formatTop(cruise.TourOperator),
    };
  }

  getFormattedCruises(searchParams: ICruiseSearchParams): Observable<ICruiseCard[]> {
    return this.getCruises(searchParams).pipe(
      map(e => e.map(x => this.formatCruise(x))),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getCruises(searchParams: ICruiseSearchParams): Observable<any[]> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
      this._projectService.getAgencyNetwork(),
      this._projectService.hasOnlyNetQuotes(),
      this._projectService.getQuoteType()
    ]).pipe(
      mergeMap(e => {
        const hasOnlyNetQuotes = e[3];
        const quoteType = e[4];
        const url = `${e[0].searchUrl}/index.php/cruise/${e[1]}/search/slim/from/${searchParams.from}/to/${searchParams.to}/destinations/${searchParams.destinations}/top/${searchParams.top}/ships/${searchParams.ships}/harbours/${searchParams.harbours}/durations/${searchParams.durations}/price/${searchParams.price}/promo/${searchParams.promo}/agency/${searchParams.agency}/network/${searchParams.network}/onlyNetworkQuotes/${hasOnlyNetQuotes}/quoteType/${quoteType}?bestCabinOnly=${searchParams.bestCabinOnly|| false}`;

        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this._http.get<any>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => fixArray(e.Cruise_Collection.Cruise)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }
}
